/**
 * HealthHistoryTable Component
 *
 * Displays health check history with filtering.
 *
 * Feature: 017-backup-infrastructure-setup
 * Task: T041 - Create HealthHistoryTable component
 * Constitution: v1.8.0 (Multi-Subscription SQL MI Edition)
 */

import React, { useState, useMemo } from 'react';
import { useHealthHistory } from '../hooks/useInfrastructure';
import { HealthStatus } from '../types';
import { formatEastern } from '@/utils/dateFormatter';

interface HealthHistoryTableProps {
  className?: string;
}

const COMPONENTS = [
  { value: '', label: 'All Components' },
  { value: 'acr', label: 'Container Registry' },
  { value: 'aci', label: 'Container Instances' },
  { value: 'keyVault', label: 'Key Vault' },
  { value: 'storage', label: 'Storage' },
];

const TIME_RANGES = [
  { value: '1h', label: 'Last Hour', hours: 1 },
  { value: '6h', label: 'Last 6 Hours', hours: 6 },
  { value: '24h', label: 'Last 24 Hours', hours: 24 },
  { value: '7d', label: 'Last 7 Days', hours: 168 },
];

const getStatusBadge = (status: HealthStatus | 'unknown') => {
  switch (status) {
    case 'healthy':
      return (
        <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'>
          ✓ Healthy
        </span>
      );
    case 'degraded':
      return (
        <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'>
          ⚠ Degraded
        </span>
      );
    case 'unavailable':
      return (
        <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'>
          ✗ Unavailable
        </span>
      );
    default:
      return (
        <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'>
          ? Unknown
        </span>
      );
  }
};

const getComponentLabel = (component: string): string => {
  const found = COMPONENTS.find((c) => c.value === component);
  return found?.label || component;
};

const formatTimestamp = (timestamp: string): string => {
  return formatEastern(timestamp);
};

const formatLatency = (latencyMs?: number): string => {
  if (latencyMs === undefined) return '-';
  if (latencyMs < 1000) return `${latencyMs}ms`;
  return `${(latencyMs / 1000).toFixed(1)}s`;
};

export const HealthHistoryTable: React.FC<HealthHistoryTableProps> = ({ className = '' }) => {
  const [selectedComponent, setSelectedComponent] = useState<string>('');
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('24h');
  const pageSize = 100; // Use larger limit since API doesn't support offset pagination

  // Calculate since date from time range — memoized so query key only changes when
  // the selected range changes, not on every render tick
  const since = useMemo(() => {
    const timeRange = TIME_RANGES.find((r) => r.value === selectedTimeRange);
    return timeRange
      ? new Date(Date.now() - timeRange.hours * 60 * 60 * 1000).toISOString()
      : undefined;
  }, [selectedTimeRange]);

  const { data, isLoading, error, refetch } = useHealthHistory({
    component: selectedComponent || undefined,
    since,
    limit: pageSize,
  });

  const items = data?.items || [];
  const total = data?.total || 0;

  return (
    <div className={`bg-[var(--color-bg-card)] rounded-lg shadow-sm border border-[var(--color-border)] ${className}`}>
      {/* Header */}
      <div className='px-6 py-4 border-b border-[var(--color-border)]'>
        <div className='flex items-center justify-between'>
          <div>
            <h3 className='text-lg font-medium text-[var(--color-text-primary)]'>Health Check History</h3>
            <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>
              {total} check{total !== 1 ? 's' : ''} recorded
            </p>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className='p-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] rounded-full hover:bg-[var(--color-bg-subtle)]'
            title='Refresh'
          >
            <svg
              className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`}
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
              />
            </svg>
          </button>
        </div>

        {/* Filters */}
        <div className='mt-4 flex items-center space-x-4'>
          <div>
            <label htmlFor='component-filter' className='sr-only'>
              Filter by component
            </label>
            <select
              id='component-filter'
              value={selectedComponent}
              onChange={(e) => {
                setSelectedComponent(e.target.value);
              }}
              className='px-3 py-1.5 text-sm border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'
            >
              {COMPONENTS.map((comp) => (
                <option key={comp.value} value={comp.value}>
                  {comp.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor='time-filter' className='sr-only'>
              Time range
            </label>
            <select
              id='time-filter'
              value={selectedTimeRange}
              onChange={(e) => {
                setSelectedTimeRange(e.target.value);
              }}
              className='px-3 py-1.5 text-sm border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'
            >
              {TIME_RANGES.map((range) => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className='p-8 text-center'>
          <svg
            className='animate-spin h-8 w-8 text-[var(--color-accent)] mx-auto mb-4'
            fill='none'
            viewBox='0 0 24 24'
          >
            <circle
              className='opacity-25'
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='4'
            />
            <path
              className='opacity-75'
              fill='currentColor'
              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
            />
          </svg>
          <p className='text-[var(--color-text-secondary)]'>Loading history...</p>
        </div>
      ) : error ? (
        <div className='p-8 text-center text-red-600 dark:text-red-400'>
          Failed to load health history: {error.message}
        </div>
      ) : items.length === 0 ? (
        <div className='p-8 text-center text-[var(--color-text-secondary)]'>
          No health checks recorded for the selected filters.
        </div>
      ) : (
        <>
          <div className='overflow-x-auto'>
            <table className='min-w-full divide-y divide-[var(--color-border)]'>
              <thead className='bg-[var(--color-bg-subtle)]'>
                <tr>
                  <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                    Timestamp
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                    Component
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                    Status
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                    Message
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                    Latency
                  </th>
                </tr>
              </thead>
              <tbody className='bg-[var(--color-bg-card)] divide-y divide-[var(--color-border)]'>
                {items.map((item) => (
                  <tr key={item.id} className='hover:bg-[var(--color-bg-subtle)]'>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)]'>
                      {formatTimestamp(item.checkedAt)}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-[var(--color-text-primary)]'>
                      {getComponentLabel(item.component)}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      {getStatusBadge(item.status as HealthStatus)}
                    </td>
                    <td
                      className='px-6 py-4 text-sm text-[var(--color-text-secondary)] max-w-xs truncate'
                      title={item.message ?? undefined}
                    >
                      {item.message ?? '-'}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)]'>
                      {formatLatency(item.responseTimeMs ?? undefined)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Results info */}
          <div className='px-6 py-4 border-t border-[var(--color-border)]'>
            <p className='text-sm text-[var(--color-text-secondary)]'>
              Showing {items.length} of {total} check{total !== 1 ? 's' : ''}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default HealthHistoryTable;
